/**
 * 智能体团队配置模态框
 *
 * 支持配置规划者和监督者的：
 * - 显示名称
 * - 提示词
 * - 可用工具
 * - 温度参数
 * - LLM配置
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '../ui/Modal';
import { useAgentTeamConfigStore } from '../../store/agentTeamConfigStore';
import { RoleConfigForm } from './RoleConfigForm';

interface ConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: number;
}

type ActiveTab = 'planner' | 'supervisor';

export const ConfigModal: React.FC<ConfigModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
}) => {
  const {
    loadConfig,
    saveConfig,
    resetConfig,
    plannerConfig,
    supervisorConfig,
    loading,
    saving,
    error,
    clearError,
  } = useAgentTeamConfigStore();

  const [activeTab, setActiveTab] = useState<ActiveTab>('planner');
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetTarget, setResetTarget] = useState<'planner' | 'supervisor' | 'all' | null>(null);

  useEffect(() => {
    if (isOpen) {
      clearError();
      loadConfig(workspaceId);
    }
  }, [isOpen, workspaceId, loadConfig, clearError]);

  const handleSave = useCallback(async () => {
    try {
      await saveConfig(activeTab);
      onClose();
    } catch (err) {
      console.error('保存配置失败:', err);
    }
  }, [activeTab, saveConfig, onClose]);

  const handleResetRequest = useCallback((target: 'planner' | 'supervisor' | 'all') => {
    setResetTarget(target);
    setShowResetConfirm(true);
  }, []);

  const handleResetConfirm = useCallback(async () => {
    if (!resetTarget) return;
    
    try {
      if (resetTarget === 'all') {
        await resetConfig();
      } else {
        await resetConfig(resetTarget);
      }
      setShowResetConfirm(false);
      setResetTarget(null);
    } catch (err) {
      console.error('重置配置失败:', err);
      setShowResetConfirm(false);
      setResetTarget(null);
    }
  }, [resetTarget, resetConfig]);

  const handleResetCancel = useCallback(() => {
    setShowResetConfirm(false);
    setResetTarget(null);
  }, []);

  const handleCancel = useCallback(() => {
    clearError();
    onClose();
  }, [clearError, onClose]);

  const currentConfig = activeTab === 'planner' ? plannerConfig : supervisorConfig;

  const tabButtons = [
    {
      role: 'planner' as ActiveTab,
      label: '规划者',
      config: plannerConfig,
    },
    {
      role: 'supervisor' as ActiveTab,
      label: '监督者',
      config: supervisorConfig,
    },
  ];

  const footer = (
    <>
      <button
        type="button"
        onClick={() => handleResetRequest(activeTab)}
        className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md border border-gray-300"
        disabled={loading || saving}
      >
        重置当前
      </button>
      <button
        type="button"
        onClick={() => handleResetRequest('all')}
        className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md border border-gray-300"
        disabled={loading || saving}
      >
        重置全部
      </button>
      <button
        type="button"
        onClick={handleCancel}
        className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
        disabled={saving}
      >
        取消
      </button>
      <button
        type="button"
        onClick={handleSave}
        className="px-4 py-2 bg-ink-800 hover:bg-ink-700 text-white rounded-md disabled:opacity-50"
        disabled={loading || saving}
      >
        {saving ? '保存中...' : '保存'}
      </button>
    </>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleCancel}
        title="智能体团队配置"
        size="lg"
        footer={footer}
        closeOnOverlayClick={!saving}
        closeOnEsc={!saving}
      >
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}

        {loading && !plannerConfig && !supervisorConfig ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-700">加载配置中...</div>
          </div>
        ) : (
          <>
            <div className="flex gap-2 mb-6">
              {tabButtons.map((tab) => (
                <button
                  key={tab.role}
                  onClick={() => setActiveTab(tab.role)}
                  className={`
                    px-4 py-2 rounded-md font-medium transition-colors
                    ${activeTab === tab.role
                      ? 'bg-ink-800 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }
                  `}
                >
                  {tab.label}
                  {tab.config && (
                    <span className="ml-2 text-xs opacity-80">
                      ({tab.config.display_name})
                    </span>
                  )}
                </button>
              ))}
            </div>

            <RoleConfigForm
              role={activeTab}
              config={currentConfig}
              disabled={loading || saving}
            />

            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-700">
                当前配置: {activeTab === 'planner' ? '规划者' : '监督者'}
              </p>
              {currentConfig && (
                <p className="text-xs text-gray-600 mt-1">
                  最后更新: {currentConfig.updated_at || '未保存'}
                </p>
              )}
            </div>
          </>
        )}
      </Modal>

      {showResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              确认重置配置
            </h3>
            <p className="text-gray-700 mb-6">
              {resetTarget === 'all'
                ? '确定要将所有配置重置为默认值吗？此操作不可撤销。'
                : `确定要将${resetTarget === 'planner' ? '规划者' : '监督者'}配置重置为默认值吗？此操作不可撤销。`
              }
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleResetCancel}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
              >
                取消
              </button>
              <button
                onClick={handleResetConfirm}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md"
              >
                确认重置
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConfigModal;